// ============================================
// MY POSITIONS PAGE
// View and manage all tracked positions
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserContext';
import { ActivePosition, PositionSummary } from '../types';
import {
  fetchPositions,
  fetchPositionSummary,
  closePosition,
  deletePosition,
  updatePosition,
  positionPollingService,
  notificationService
} from '../services/positionService';
import { PositionCard } from '../components/PositionCard';
import {
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  Bell,
  BellOff,
  RefreshCw,
  Filter,
  BarChart3,
  Plus,
  Lock,
  Clock
} from 'lucide-react';

type FilterStatus = 'all' | 'ACTIVE' | 'CLOSED_PROFIT' | 'CLOSED_LOSS';

export const PositionsPage: React.FC = () => {
  const { user, isSignedIn, isLoaded } = useUser();
  const { userData, loading: userLoading } = useUserData();

  const [positions, setPositions] = useState<ActivePosition[]>([]);
  const [summary, setSummary] = useState<PositionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [marketOpen, setMarketOpen] = useState(false);
  const [nextMarketOpen, setNextMarketOpen] = useState<Date | null>(null);

  const clerkId = user?.id;

  // Fetch positions
  const loadPositions = useCallback(async () => {
    if (!clerkId) return;

    try {
      setLoading(true);
      const [positionsData, summaryData] = await Promise.all([
        fetchPositions(clerkId, filter === 'all' ? undefined : filter),
        fetchPositionSummary(clerkId)
      ]);
      setPositions(positionsData);
      setSummary(summaryData);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load positions');
    } finally {
      setLoading(false);
    }
  }, [clerkId, filter]);

  // Initial load
  useEffect(() => {
    if (clerkId) {
      loadPositions();
    }
  }, [clerkId, loadPositions]);

  // Set up notifications and polling
  useEffect(() => {
    if (!clerkId || positions.filter(p => p.status === 'ACTIVE').length === 0) {
      return;
    }

    // Request notification permission
    notificationService.requestPermission().then((granted) => {
      setNotificationsEnabled(granted);
    });

    // Check initial market status
    setMarketOpen(positionPollingService.isMarketCurrentlyOpen());
    if (!positionPollingService.isMarketCurrentlyOpen()) {
      setNextMarketOpen(positionPollingService.getNextMarketOpenTime());
    }

    // Start polling
    positionPollingService.start(clerkId, {
      onPriceUpdate: (position, newPrice, pnl, pnlPercent) => {
        setPositions(prev => prev.map(p =>
          p.id === position.id
            ? { ...p, currentPrice: newPrice, currentPnL: pnl, currentPnLPercent: pnlPercent, lastPriceUpdate: new Date().toISOString() }
            : p
        ));
        setLastUpdate(new Date());
      },
      onStopHit: (position, currentPrice) => {
        notificationService.notifyStopHit(position, currentPrice);
      },
      onProfitTarget: (position, targetPercent, currentPrice) => {
        notificationService.notifyProfitTarget(position, targetPercent, currentPrice);
      },
      onDTEWarning: (position, daysRemaining) => {
        notificationService.notifyDTEWarning(position, daysRemaining);
      },
      onMarketClosed: (nextOpen) => {
        setMarketOpen(false);
        setNextMarketOpen(nextOpen);
      },
      onMarketOpen: () => {
        setMarketOpen(true);
        setNextMarketOpen(null);
      },
      onError: (err, position) => {
        console.error('[PositionsPage] Polling error:', err, position);
      }
    }, 5 * 60 * 1000); // 5 minutes

    setPollingActive(true);

    return () => {
      positionPollingService.stop();
      setPollingActive(false);
    };
  }, [clerkId, positions.length]);

  // Handlers
  const handleClosePosition = async (positionId: number, pnl?: number) => {
    if (!clerkId) return;

    try {
      await closePosition(clerkId, positionId, undefined, pnl);
      await loadPositions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close position');
    }
  };

  const handleDeletePosition = async (positionId: number) => {
    if (!clerkId) return;

    if (!confirm('Are you sure you want to delete this position? This cannot be undone.')) {
      return;
    }

    try {
      await deletePosition(clerkId, positionId);
      await loadPositions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete position');
    }
  };

  const handleToggleAlerts = async (positionId: number, enabled: boolean) => {
    if (!clerkId) return;

    try {
      await updatePosition(clerkId, positionId, { alertsEnabled: enabled });
      setPositions(prev => prev.map(p =>
        p.id === positionId ? { ...p, alertsEnabled: enabled } : p
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update alerts');
    }
  };

  const handleUpdateNotes = async (positionId: number, notes: string) => {
    if (!clerkId) return;

    try {
      await updatePosition(clerkId, positionId, { notes });
      setPositions(prev => prev.map(p =>
        p.id === positionId ? { ...p, notes } : p
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update notes');
    }
  };

  // Loading state
  if (!isLoaded || userLoading) {
    return (
      <div className="min-h-screen bg-terminal-bg flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-terminal-dim">Loading positions...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  if (!isSignedIn) {
    return (
      <div className="min-h-screen bg-terminal-bg flex flex-col items-center justify-center p-4">
        <div className="max-w-md text-center">
          <Lock className="w-16 h-16 text-terminal-dim mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Sign In Required</h1>
          <p className="text-terminal-dim mb-6">Please sign in to view your positions.</p>
          <Link
            to="/login"
            className="block w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold hover:opacity-90"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  const filteredPositions = positions;
  const activeCount = positions.filter(p => p.status === 'ACTIVE').length;

  return (
    <div className="min-h-screen bg-terminal-bg text-terminal-text font-mono">
      {/* Scanline Effect */}
      <div className="fixed inset-0 pointer-events-none z-50 bg-scanlines opacity-[0.02]" />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                  7S
                </div>
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              {/* Market & Polling Status */}
              <div className={`flex items-center gap-2 text-xs ${marketOpen ? 'text-emerald-400' : 'text-amber-400'}`}>
                <span className={`w-2 h-2 rounded-full ${marketOpen ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
                {marketOpen ? (
                  pollingActive ? 'Live' : 'Paused'
                ) : (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Market Closed
                  </span>
                )}
              </div>

              {/* Notifications */}
              <button
                onClick={() => notificationService.requestPermission().then(setNotificationsEnabled)}
                className={`p-2 rounded-lg ${notificationsEnabled ? 'bg-cyan-500/10 text-cyan-400' : 'bg-slate-800 text-terminal-dim'}`}
                title={notificationsEnabled ? 'Notifications enabled' : 'Click to enable notifications'}
              >
                {notificationsEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </button>

              <Link to="/dashboard" className="text-terminal-dim hover:text-white text-sm">
                Dashboard
              </Link>
              <Link to="/scanner" className="text-terminal-dim hover:text-white text-sm">
                Scanner
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">My Positions</h1>
            <p className="text-terminal-dim">
              Track and manage your active trades
              {lastUpdate && (
                <span className="ml-2 text-xs">
                  Last updated: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => loadPositions()}
              disabled={loading}
              className="p-3 bg-slate-800 text-terminal-dim hover:text-white rounded-lg disabled:opacity-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Refresh positions"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link
              to="/scanner"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-lg hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              New Trade
            </Link>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-3 sm:p-4">
              <div className="text-xs text-terminal-dim uppercase mb-1">Active</div>
              <div className="text-xl sm:text-2xl font-bold text-cyan-400">{summary.activePositions}</div>
            </div>
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-3 sm:p-4">
              <div className="text-xs text-terminal-dim uppercase mb-1">Total P&L</div>
              <div className={`text-xl sm:text-2xl font-bold ${summary.totalPnL >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {summary.totalPnL >= 0 ? '+' : ''}${summary.totalPnL.toFixed(0)}
              </div>
            </div>
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-3 sm:p-4">
              <div className="text-xs text-terminal-dim uppercase mb-1">Win Rate</div>
              <div className={`text-xl sm:text-2xl font-bold ${summary.winRate >= 50 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {summary.winRate.toFixed(1)}%
              </div>
            </div>
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-3 sm:p-4">
              <div className="text-xs text-terminal-dim uppercase mb-1">Avg Win</div>
              <div className="text-xl sm:text-2xl font-bold text-emerald-400">+${summary.avgWin.toFixed(0)}</div>
            </div>
            <div className="bg-terminal-card border border-terminal-border rounded-xl p-3 sm:p-4 col-span-2 sm:col-span-1">
              <div className="text-xs text-terminal-dim uppercase mb-1">Payoff Ratio</div>
              <div className={`text-xl sm:text-2xl font-bold ${summary.payoffRatio >= 1 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {summary.payoffRatio.toFixed(2)}
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center gap-1.5 sm:gap-2 mb-6 border-b border-terminal-border pb-4 overflow-x-auto">
          <Filter className="w-4 h-4 text-terminal-dim flex-shrink-0" />
          {(['all', 'ACTIVE', 'CLOSED_PROFIT', 'CLOSED_LOSS'] as FilterStatus[]).map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-2 sm:py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-h-[44px] sm:min-h-0 ${
                filter === status
                  ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30'
                  : 'text-terminal-dim hover:text-white'
              }`}
            >
              {status === 'all' ? 'All' : status === 'ACTIVE' ? 'Active' : status === 'CLOSED_PROFIT' ? 'Winners' : 'Losers'}
              {status === 'ACTIVE' && activeCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-cyan-500/20 rounded text-xs">
                  {activeCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-400 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Positions List */}
        {loading && positions.length === 0 ? (
          <div className="text-center py-12">
            <div className="h-8 w-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-terminal-dim">Loading positions...</p>
          </div>
        ) : filteredPositions.length === 0 ? (
          <div className="text-center py-12 bg-terminal-card border border-terminal-border rounded-xl">
            <BarChart3 className="w-16 h-16 text-terminal-dim mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">No Positions Yet</h2>
            <p className="text-terminal-dim mb-6">
              {filter === 'all'
                ? "You haven't tracked any trades yet."
                : `No ${filter.toLowerCase().replace('_', ' ')} positions found.`}
            </p>
            <Link
              to="/scanner"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black font-bold rounded-lg hover:opacity-90"
            >
              <Plus className="w-5 h-5" />
              Scan for Trades
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPositions.map((position) => (
              <PositionCard
                key={position.id}
                position={position}
                onClose={handleClosePosition}
                onDelete={handleDeletePosition}
                onToggleAlerts={handleToggleAlerts}
                onUpdateNotes={handleUpdateNotes}
              />
            ))}
          </div>
        )}

        {/* Market Status Banner */}
        {!marketOpen && nextMarketOpen && (
          <div className="mt-8 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="text-sm">
                <strong className="text-amber-400">Market Closed</strong>
                <span className="text-terminal-dim ml-2">
                  Next open: {nextMarketOpen.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-4 p-4 bg-slate-800/50 border border-terminal-border rounded-xl">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-terminal-dim">
              <strong className="text-amber-400">Price Polling:</strong> Prices update every 5 minutes during US market hours (Mon-Fri 9:30 AM - 4:00 PM Eastern, auto-adjusts for DST).
              Polling pauses automatically outside trading hours and on US holidays.
              For real-time execution, enter trades directly in your broker.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PositionsPage;
